import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatElapsed, type TurnPhase } from "@/features/chat/lib/turnPhase";

type TurnProgressStripProps = {
  phase: TurnPhase | null;
  elapsedSeconds: number;
  /** Switches the response to its Processing tab. */
  onShowDetails?: () => void;
};

/**
 * One line above early data (table, chart, SQL) while the rest of the answer is written:
 * what the turn is doing now, how long it has been, and a link to the step-by-step log.
 *
 * The data tab is where the reader lands as soon as rows arrive, so this is the only sign
 * that the turn is still running; without it an answer-in-progress looks finished.
 */
export function TurnProgressStrip({ phase, elapsedSeconds, onShowDetails }: TurnProgressStripProps) {
  const { t } = useTranslation("chat");
  const label = phase ? t(`phases.${phase}`) : t("processingTitle");
  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1.5,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor: "var(--theme-background)",
        border: "1px solid var(--theme-border)",
      }}
    >
      <CircularProgress size={12} thickness={5} sx={{ color: "var(--theme-primary)" }} />
      <Typography variant="caption" sx={{ color: "var(--theme-text-primary)", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "var(--theme-text-secondary)", fontVariantNumeric: "tabular-nums" }}
      >
        · {formatElapsed(elapsedSeconds)}
      </Typography>
      {onShowDetails ? (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={onShowDetails}
          sx={{ ml: "auto", color: "var(--theme-primary)" }}
        >
          {t("processing.details")}
        </Link>
      ) : null}
    </Box>
  );
}
